import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { userManagementAPI } from '../services/api';
import ConfirmModal from '../components/user-management/ConfirmModal';
import AddToOrgModal from '../components/user-management/AddToOrgModal';
import AddToGroupModal from '../components/user-management/AddToGroupModal';
import GrantRoleModal from '../components/user-management/GrantRoleModal';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ShieldExclamationIcon } from '@heroicons/react/24/solid';

// ============================================================================
// Helpers
// ============================================================================

function StatusBadge({ status, size = 'sm' }) {
  const styles = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-yellow-100 text-yellow-800',
    deleted: 'bg-red-100 text-red-800',
  };
  const sizeClass = size === 'lg' ? 'text-sm px-3 py-1' : 'text-xs px-2 py-0.5';
  return (
    <span className={`font-medium rounded-full ${sizeClass} ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'unknown'}
    </span>
  );
}

function RoleBadge({ role }) {
  const styles = {
    org_admin: 'bg-red-100 text-red-700',
    campus_pastor: 'bg-orange-100 text-orange-700',
    ministry_director: 'bg-purple-100 text-purple-700',
    group_leader: 'bg-blue-100 text-blue-700',
    group_member: 'bg-gray-100 text-gray-600',
  };
  const labels = {
    org_admin: 'Org Admin',
    campus_pastor: 'Campus Pastor',
    ministry_director: 'Ministry Director',
    group_leader: 'Group Leader',
    group_member: 'Group Member',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[role] || 'bg-gray-100 text-gray-600'}`}>
      {labels[role] || role}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg transition-all ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? (
        <CheckCircleIcon className="w-5 h-5" />
      ) : (
        <ExclamationTriangleIcon className="w-5 h-5" />
      )}
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2">
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Tab Components
// ============================================================================

function UserProfileTab({ user, onSave }) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    status: 'active',
  });
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        status: user.status || 'active',
      });
    }
  }, [user]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveResult(null);
    try {
      await userManagementAPI.updateUser(user.id, form);
      setSaveResult({ type: 'success', message: 'Changes saved successfully' });
      if (onSave) onSave();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save changes';
      setSaveResult({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-storm-blue mb-1">First Name</label>
          <input
            type="text"
            value={form.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            className="w-full px-4 py-3 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-storm-blue mb-1">Last Name</label>
          <input
            type="text"
            value={form.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            className="w-full px-4 py-3 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px]"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-storm-blue mb-1">Email</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => handleChange('email', e.target.value)}
          className="w-full px-4 py-3 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px]"
        />
        <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
          <ExclamationTriangleIcon className="w-3 h-3" />
          Email changes also update Firebase Auth login credentials
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-storm-blue mb-1">Phone</label>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          className="w-full px-4 py-3 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px]"
          placeholder="(555) 123-4567"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-storm-blue mb-1">Status</label>
        <select
          value={form.status}
          onChange={(e) => handleChange('status', e.target.value)}
          className="w-full px-4 py-3 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px] bg-white"
        >
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      {saveResult && (
        <div className={`px-4 py-3 rounded-xl border text-sm ${
          saveResult.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {saveResult.message}
        </div>
      )}

      <button
        type="submit"
        disabled={saving}
        className={`min-h-touch px-6 py-3 rounded-button font-semibold text-cloud-white transition-all active:scale-[0.98] ${
          saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-button shadow-button'
        }`}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
}

function UserOrgsTab({ user, orgTree, onRefresh }) {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast] = useState(null);

  const orgs = user?.organizations || [];
  const currentOrgIds = orgs.map((o) => o.organization_id || o.id);

  const handleRemoveOrg = async (orgId, orgName) => {
    setConfirmModal({
      title: 'Remove from Organization',
      message: `Remove this user from "${orgName}"? This will also remove them from all groups and roles within this organization.`,
      confirmText: 'Remove',
      onConfirm: async () => {
        try {
          await userManagementAPI.removeFromOrganization(user.id, orgId);
          setToast({ type: 'success', message: `Removed from ${orgName}` });
          onRefresh();
        } catch (err) {
          setToast({ type: 'error', message: err.response?.data?.error || err.response?.data?.message || 'Failed to remove' });
        }
        setConfirmModal(null);
      },
    });
  };

  const handleAddOrg = async (orgId) => {
    try {
      await userManagementAPI.addToOrganization(user.id, orgId);
      setToast({ type: 'success', message: 'Added to organization' });
      onRefresh();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || err.response?.data?.message || 'Failed to add' });
      throw err;
    }
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-deep-navy">Organizations ({orgs.length})</h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="min-h-touch px-4 py-2 rounded-button text-sm font-semibold text-storm-blue border-2 border-storm-blue hover:bg-storm-blue hover:text-white transition-all active:scale-[0.98] flex items-center gap-1"
        >
          <PlusIcon className="w-4 h-4" />
          Add to Organization
        </button>
      </div>

      {orgs.length === 0 ? (
        <p className="text-center text-sky-blue py-8">Not a member of any organization</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          {orgs.map((org) => (
            <div key={org.organization_id || org.id} className="flex items-center justify-between px-4 py-3 min-h-touch hover:bg-gray-50 transition-all">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-deep-navy truncate">{org.organization_name || org.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  {org.tier && (
                    <span className="text-xs font-medium text-storm-blue bg-blue-50 px-2 py-0.5 rounded-full">{org.tier}</span>
                  )}
                  <StatusBadge status={org.status} />
                  <span className="text-xs text-sky-blue">Joined {formatDate(org.joined_at || org.created_at)}</span>
                </div>
              </div>
              <button
                onClick={() => handleRemoveOrg(org.organization_id || org.id, org.organization_name || org.name)}
                className="min-h-touch min-w-touch flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                title="Remove from organization"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200">
        <Link
          to="/user-deletion"
          className="text-sm font-semibold text-red-600 hover:text-red-700 transition-colors"
        >
          Delete User Entirely &rarr;
        </Link>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={confirmModal?.onConfirm}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmText={confirmModal?.confirmText}
      />

      <AddToOrgModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onConfirm={handleAddOrg}
        orgTree={orgTree}
        currentOrgIds={currentOrgIds}
      />
    </div>
  );
}

function UserGroupsTab({ user, orgTree, onRefresh }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast] = useState(null);

  const groups = user?.groups || [];
  const currentGroupIds = groups.map((g) => g.group_id || g.id);

  // Group by organization
  const groupedByOrg = {};
  for (const group of groups) {
    const orgName = group.org_name || group.organization_name || 'Unknown Organization';
    if (!groupedByOrg[orgName]) groupedByOrg[orgName] = [];
    groupedByOrg[orgName].push(group);
  }

  const handleRemoveGroup = async (groupId, groupName) => {
    setConfirmModal({
      title: 'Remove from Group',
      message: `Remove this user from "${groupName}"?`,
      confirmText: 'Remove',
      onConfirm: async () => {
        try {
          await userManagementAPI.removeFromGroup(user.id, groupId);
          setToast({ type: 'success', message: `Removed from ${groupName}` });
          onRefresh();
        } catch (err) {
          setToast({ type: 'error', message: err.response?.data?.error || err.response?.data?.message || 'Failed to remove' });
        }
        setConfirmModal(null);
      },
    });
  };

  const handleAddGroups = async (groupIds) => {
    try {
      for (const groupId of groupIds) {
        await userManagementAPI.addToGroup(user.id, groupId);
      }
      setToast({ type: 'success', message: `Added to ${groupIds.length} group${groupIds.length > 1 ? 's' : ''}` });
      onRefresh();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || err.response?.data?.message || 'Failed to add to group(s)' });
      throw err;
    }
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-deep-navy">Groups ({groups.length})</h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="min-h-touch px-4 py-2 rounded-button text-sm font-semibold text-storm-blue border-2 border-storm-blue hover:bg-storm-blue hover:text-white transition-all active:scale-[0.98] flex items-center gap-1"
        >
          <PlusIcon className="w-4 h-4" />
          Add to Groups
        </button>
      </div>

      {groups.length === 0 ? (
        <p className="text-center text-sky-blue py-8">Not a member of any group</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByOrg).map(([orgName, orgGroups]) => (
            <div key={orgName}>
              <h4 className="text-sm font-semibold text-storm-blue uppercase tracking-wider mb-2">{orgName}</h4>
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                {orgGroups.map((group) => (
                  <div key={group.group_id || group.id} className="flex items-center justify-between px-4 py-3 min-h-touch hover:bg-gray-50 transition-all">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-deep-navy truncate">{group.group_name || group.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <RoleBadge role={group.role} />
                        <span className="text-xs text-sky-blue">Joined {formatDate(group.joined_at || group.created_at)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveGroup(group.group_id || group.id, group.group_name || group.name)}
                      className="min-h-touch min-w-touch flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                      title="Remove from group"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={confirmModal?.onConfirm}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmText={confirmModal?.confirmText}
      />

      <AddToGroupModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onConfirm={handleAddGroups}
        orgTree={orgTree}
        currentGroupIds={currentGroupIds}
      />
    </div>
  );
}

function UserRolesTab({ user, orgTree, onRefresh }) {
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast] = useState(null);

  const roles = user?.roles || [];

  const handleRevokeRole = async (roleId, roleName) => {
    setConfirmModal({
      title: 'Revoke Role',
      message: `Revoke "${roleName}" role from this user?`,
      confirmText: 'Revoke',
      onConfirm: async () => {
        try {
          await userManagementAPI.revokeRole(user.id, roleId);
          setToast({ type: 'success', message: `Revoked ${roleName}` });
          onRefresh();
        } catch (err) {
          setToast({ type: 'error', message: err.response?.data?.error || err.response?.data?.message || 'Failed to revoke role' });
        }
        setConfirmModal(null);
      },
    });
  };

  const handleGrantRole = async (roleData) => {
    try {
      await userManagementAPI.grantRole(user.id, roleData);
      setToast({ type: 'success', message: `Granted ${roleData.role}` });
      onRefresh();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || err.response?.data?.message || 'Failed to grant role' });
      throw err;
    }
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-deep-navy">Roles ({roles.length})</h3>
        <button
          onClick={() => setShowGrantModal(true)}
          className="min-h-touch px-4 py-2 rounded-button text-sm font-semibold text-storm-blue border-2 border-storm-blue hover:bg-storm-blue hover:text-white transition-all active:scale-[0.98] flex items-center gap-1"
        >
          <PlusIcon className="w-4 h-4" />
          Grant Role
        </button>
      </div>

      {roles.length === 0 ? (
        <p className="text-center text-sky-blue py-8">No active roles assigned</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid md:grid-cols-6 gap-2 px-4 py-2 bg-gray-50 text-xs font-semibold text-storm-blue uppercase tracking-wider">
            <span>Role</span>
            <span>Scope Type</span>
            <span>Scope Name</span>
            <span>Organization</span>
            <span>Granted</span>
            <span className="text-right">Actions</span>
          </div>

          {roles.map((role) => (
            <div key={role.id} className="flex flex-col md:grid md:grid-cols-6 gap-2 px-4 py-3 min-h-touch hover:bg-gray-50 transition-all items-center">
              <div>
                <RoleBadge role={role.role} />
              </div>
              <div className="text-sm text-deep-navy">{role.scope_type || '-'}</div>
              <div className="text-sm text-deep-navy truncate">{role.campus_name || role.ministry_name || role.group_name || role.scope_name || '-'}</div>
              <div className="text-sm text-sky-blue truncate">{role.org_name || role.organization_name || '-'}</div>
              <div className="text-xs text-sky-blue">{formatDate(role.granted_at || role.created_at)}</div>
              <div className="flex justify-end">
                <button
                  onClick={() => handleRevokeRole(role.id, role.role)}
                  className="min-h-touch min-w-touch flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                  title="Revoke role"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={confirmModal?.onConfirm}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmText={confirmModal?.confirmText}
      />

      <GrantRoleModal
        isOpen={showGrantModal}
        onClose={() => setShowGrantModal(false)}
        onConfirm={handleGrantRole}
        orgTree={orgTree}
        userId={user?.id}
      />
    </div>
  );
}

// ============================================================================
// Abuse History Tab
// ============================================================================

function FlagStatusBadge({ status }) {
  const styles = {
    new: 'bg-red-100 text-red-700',
    reviewed: 'bg-blue-100 text-blue-700',
    dismissed: 'bg-gray-100 text-gray-600',
    suspended: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function AbuseHistoryTab({ alerts, onRefresh }) {
  if (alerts.length === 0) {
    return <p className="text-center text-sky-blue py-8">No abuse flags recorded for this user</p>;
  }

  const newCount = alerts.filter(a => a.status === 'new').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-deep-navy">
          All Abuse Flags ({alerts.length})
          {newCount > 0 && (
            <span className="ml-2 text-sm font-medium text-red-600">
              {newCount} unresolved
            </span>
          )}
        </h3>
        <button
          onClick={onRefresh}
          className="min-h-touch px-4 py-2 rounded-button text-sm font-semibold text-storm-blue hover:bg-gray-100 transition-all flex items-center gap-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`border rounded-xl p-5 transition-all ${
              alert.status === 'new'
                ? 'border-red-200 bg-red-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            {/* Header row: type + status + timestamp */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldExclamationIcon className={`w-5 h-5 ${alert.status === 'new' ? 'text-red-600' : 'text-gray-400'}`} />
                <span className="font-semibold text-deep-navy">
                  {alert.flag_type === 'search_daily_limit' ? 'Search Rate Limit Exceeded' :
                   alert.flag_type === 'join_request_daily_limit' ? 'Join Request Rate Limit Exceeded' :
                   alert.flag_type.replace(/_/g, ' ')}
                </span>
                <FlagStatusBadge status={alert.status} />
              </div>
              <span className="text-sm text-sky-blue">
                {new Date(alert.created_at).toLocaleString()}
              </span>
            </div>

            {/* Volume stats */}
            <div className="flex gap-4 mb-3">
              {alert.details?.totalSearches24h && (
                <div className="bg-white border border-red-100 rounded-lg px-3 py-2">
                  <p className="text-2xl font-bold text-red-700">{alert.details.totalSearches24h}</p>
                  <p className="text-xs text-storm-blue">searches in 24h</p>
                  <p className="text-xs text-sky-blue">limit: 20</p>
                </div>
              )}
              {alert.details?.totalJoinRequests24h && (
                <div className="bg-white border border-red-100 rounded-lg px-3 py-2">
                  <p className="text-2xl font-bold text-red-700">{alert.details.totalJoinRequests24h}</p>
                  <p className="text-xs text-storm-blue">join requests in 24h</p>
                  <p className="text-xs text-sky-blue">limit: 3</p>
                </div>
              )}
            </div>

            {/* Search queries */}
            {alert.details?.recentQueries?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-storm-blue uppercase tracking-wider mb-2">
                  Search queries ({alert.details.recentQueries.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {alert.details.recentQueries.map((q, i) => (
                    <span key={i} className="text-xs bg-gray-100 text-deep-navy px-2.5 py-1 rounded-full border border-gray-200">
                      &ldquo;{q}&rdquo;
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Join requests */}
            {alert.details?.recentJoinRequests?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-storm-blue uppercase tracking-wider mb-2">
                  Organizations requested ({alert.details.recentJoinRequests.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {alert.details.recentJoinRequests.map((org, i) => (
                    <span key={i} className="text-xs bg-blue-50 text-deep-navy px-2.5 py-1 rounded-full border border-blue-100">
                      {org}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* IP + review footer */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-3">
              <div className="flex items-center gap-3">
                {alert.ip_address && (
                  <span className="text-xs text-sky-blue font-mono">IP: {alert.ip_address}</span>
                )}
              </div>
              {alert.reviewed_at && (
                <span className="text-xs text-sky-blue">
                  {alert.status === 'dismissed' ? 'Dismissed' : alert.status === 'suspended' ? 'Marked suspended' : 'Reviewed'}
                  {' by '}{alert.reviewed_by || 'unknown'}
                  {' on '}{new Date(alert.reviewed_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'organizations', label: 'Organizations' },
  { id: 'groups', label: 'Groups' },
  { id: 'roles', label: 'Roles' },
  { id: 'abuse', label: 'Abuse History' },
];

export default function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  // Org tree for modals
  const [orgTree, setOrgTree] = useState([]);
  const [abuseAlerts, setAbuseAlerts] = useState([]);
  const [suspendLoading, setSuspendLoading] = useState(false);

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await userManagementAPI.getUser(id);
      const data = res.data;
      // Merge user profile with organizations/groups/roles so tab components can access user.organizations, etc.
      setUser({
        ...(data.user || data),
        organizations: data.organizations || [],
        groups: data.groups || [],
        roles: data.roles || [],
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load user';
      setError(msg);
      console.error('[UserDetail] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadOrgTree = useCallback(async () => {
    try {
      const res = await userManagementAPI.getOrgTree();
      setOrgTree(res.data.organizations || res.data || []);
    } catch (err) {
      console.error('[UserDetail] Failed to load org tree:', err);
    }
  }, []);

  const loadAbuseAlerts = useCallback(async () => {
    try {
      const res = await userManagementAPI.getAbuseAlerts({ status: 'all', userId: id });
      setAbuseAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('[UserDetail] Abuse alerts error:', err);
    }
  }, [id]);

  const handleSuspend = async () => {
    if (!window.confirm(`Suspend ${user.name || user.email}? This will revoke their Flock sessions and block login.`)) return;
    setSuspendLoading(true);
    try {
      await userManagementAPI.updateUser(id, { status: 'suspended' });
      for (const alert of abuseAlerts.filter(a => a.status === 'new')) {
        await userManagementAPI.updateAbuseAlert(alert.id, { status: 'suspended' }).catch(() => {});
      }
      loadUser();
      loadAbuseAlerts();
    } catch (err) {
      window.alert('Failed to suspend: ' + (err.response?.data?.error || err.message));
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!window.confirm(`Unsuspend ${user.name || user.email}? They will be able to log in again.`)) return;
    setSuspendLoading(true);
    try {
      await userManagementAPI.updateUser(id, { status: 'active' });
      loadUser();
    } catch (err) {
      window.alert('Failed to unsuspend: ' + (err.response?.data?.error || err.message));
    } finally {
      setSuspendLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    loadOrgTree();
    loadAbuseAlerts();
  }, [loadUser, loadOrgTree, loadAbuseAlerts]);

  // Loading state
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <ArrowPathIcon className="w-8 h-8 text-storm-blue animate-spin" />
          <span className="ml-3 text-storm-blue text-lg">Loading user details...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate('/user-management')}
          className="min-h-touch flex items-center gap-2 text-storm-blue hover:text-deep-navy transition-colors mb-6"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span className="font-semibold">User Management</span>
        </button>
        <div className="rounded-card shadow-card bg-white p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <button
            onClick={loadUser}
            className="min-h-touch px-6 py-3 rounded-button font-semibold text-cloud-white bg-gradient-button shadow-button transition-all active:scale-[0.98]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const newAbuseAlerts = abuseAlerts.filter(a => a.status === 'new');

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate('/user-management')}
        className="min-h-touch flex items-center gap-2 text-storm-blue hover:text-deep-navy transition-colors mb-6"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        <span className="font-semibold">User Management</span>
      </button>

      {/* User Header */}
      <div className="rounded-card shadow-card bg-white p-6 mb-6">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl text-cloud-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #0D1B2A 0%, #1B263B 50%, #415A77 100%)' }}
          >
            {(user.first_name?.[0] || '').toUpperCase()}{(user.last_name?.[0] || '').toUpperCase()}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-deep-navy">
                {user.first_name} {user.last_name}
              </h1>
              <StatusBadge status={user.status} size="lg" />
                {newAbuseAlerts.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                    <ShieldExclamationIcon className="w-3.5 h-3.5" />
                    {newAbuseAlerts.length} active flag{newAbuseAlerts.length !== 1 ? 's' : ''}
                  </span>
                )}
                {abuseAlerts.length > 0 && newAbuseAlerts.length === 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    <ShieldExclamationIcon className="w-3.5 h-3.5" />
                    {abuseAlerts.length} past flag{abuseAlerts.length !== 1 ? 's' : ''}
                  </span>
                )}
            </div>
            <p className="text-storm-blue mt-1">{user.email}</p>
            {user.firebase_uid && (
              <p className="text-xs text-sky-blue mt-0.5 font-mono">UID: {user.firebase_uid}</p>
            )}
          </div>

          {/* Suspend/Unsuspend button */}
              {user.status === 'suspended' ? (
                <button
                  onClick={handleUnsuspend}
                  disabled={suspendLoading}
                  className={`min-h-touch px-4 py-2 rounded-button font-semibold text-white transition-all text-sm ${
                    suspendLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {suspendLoading ? 'Processing...' : 'Unsuspend'}
                </button>
              ) : (
                <button
                  onClick={handleSuspend}
                  disabled={suspendLoading}
                  className={`min-h-touch px-4 py-2 rounded-button font-semibold text-white transition-all text-sm ${
                    suspendLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {suspendLoading ? 'Processing...' : 'Suspend User'}
                </button>
              )}

          {/* Refresh button */}
          <button
            onClick={loadUser}
            className="min-h-touch min-w-touch flex items-center justify-center rounded-lg hover:bg-gray-100 transition-all"
            title="Refresh user data"
          >
            <ArrowPathIcon className="w-5 h-5 text-storm-blue" />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex -mb-px space-x-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`min-h-touch px-5 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-all ${
                activeTab === tab.id
                  ? 'border-deep-navy text-deep-navy'
                  : 'border-transparent text-storm-blue hover:text-deep-navy hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.id === 'abuse' && abuseAlerts.length > 0 && (
                <span className={`ml-1.5 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                  newAbuseAlerts.length > 0 ? 'bg-red-500' : 'bg-gray-400'
                }`}>
                  {abuseAlerts.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="rounded-card shadow-card bg-white p-6">
        {activeTab === 'profile' && (
          <UserProfileTab user={user} onSave={loadUser} />
        )}
        {activeTab === 'organizations' && (
          <UserOrgsTab user={user} orgTree={orgTree} onRefresh={loadUser} />
        )}
        {activeTab === 'groups' && (
          <UserGroupsTab user={user} orgTree={orgTree} onRefresh={loadUser} />
        )}
        {activeTab === 'roles' && (
          <UserRolesTab user={user} orgTree={orgTree} onRefresh={loadUser} />
        )}
        {activeTab === 'abuse' && (
          <AbuseHistoryTab alerts={abuseAlerts} onRefresh={loadAbuseAlerts} />
        )}
      </div>
    </div>
  );
}
